import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Library, Search, Star, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import TemplateDetail from "@/components/library/TemplateDetail";
import { libError, retryToast } from "@/lib/libraryApi";
import { CollectionsTab, FilesTab } from "@/components/library/StudioTabs";

const MODES = ["Reception", "Production", "Interaction", "Mediation"];
const SKILLS = ["Speaking", "Listening", "Reading", "Writing"];
const EMPTY = { q: "", cefr: "", theme: "", mode: "", skill: "", grouping: "", strand: "", max_minutes: "", output: "", shelf: "" };

export default function MissionLibraryStudio() {
  const { t } = useI18n();
  const [tab, setTab] = useState("library");
  const [facets, setFacets] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [sparks, setSparks] = useState([]);
  const [open, setOpen] = useState(null);
  const [collections, setCollections] = useState([]);
  const [err, setErr] = useState(false);
  const [params] = useSearchParams();

  const loadCollections = useCallback(() => api.get("/library/collections").then((r) => setCollections(r.data.collections))
    .catch((e) => e?.response?.status !== 403 && retryToast(libError(e, t), t, loadCollections)), [t]);
  const loadFacets = () => api.get("/library/facets").then((r) => setFacets(r.data)).catch((e) => setErr(e?.response?.status === 403 ? "denied" : "error"));
  useEffect(() => {
    loadFacets();
    api.get("/library/sparks").then((r) => setSparks(r.data.sparks)).catch(() => {});
    loadCollections();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const search = useCallback(() => {
    const p = Object.fromEntries(Object.entries({ ...f, page }).filter(([, v]) => v !== ""));
    api.get("/library/missions", { params: p }).then((r) => { setRes(r.data); setErr(false); })
      .catch((e) => setErr(e?.response?.status === 403 ? "denied" : "error"));
  }, [f, page]);
  useEffect(() => { const h = setTimeout(search, 250); return () => clearTimeout(h); }, [search]);
  const set = (k, v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };
  const Sel = ({ k, opts, label }) => (
    <select className="nb-input py-1.5 text-sm" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`lib-filter-${k}`}>
      <option value="">{label}</option>{opts.map((o) => <option key={o.v ?? o} value={o.v ?? o}>{o.l ?? o}</option>)}
    </select>
  );

  if (err === "denied") return <AppLayout><div className="max-w-3xl mx-auto px-4 py-8 text-coral" data-testid="lib-no-access">{t("lib.noAccess")}</div></AppLayout>;
  const pages = res ? Math.max(1, Math.ceil(res.total / res.per)) : 1;

  return (
    <AppLayout>
      <Seo title="Mission Library · FluentLoop" noindex />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/teacher" className="text-sm text-gray-600 flex items-center gap-1"><ChevronLeft className="w-4 h-4 rtl:rotate-180" /> {t("teacher.dashboard")}</Link>
        <div className="flex items-end justify-between flex-wrap gap-3 mt-2">
          <div>
            <h1 className="font-cabinet font-extrabold text-3xl sm:text-4xl flex items-center gap-2"><Library className="w-8 h-8" /> {t("lib.title")}</h1>
            <p className="text-gray-600 text-sm mt-1">{t("lib.subtitle", { n: facets?.total ?? "…" })}</p>
          </div>
          <div className="flex gap-1" role="tablist">
            {["library", "collections", "files"].map((k) => (
              <button key={k} role="tab" onClick={() => setTab(k)} className={`nb-tag text-sm ${tab === k ? "bg-ink text-white" : "bg-white"}`} data-testid={`lib-tab-${k}`}>{t(`lib.tab.${k}`)}</button>
            ))}
          </div>
        </div>

        {tab === "library" && (
          <>
            {params.get("asset") && <div className="nb-card bg-lavender p-3 mt-4 text-sm" data-testid="lib-asset-banner">{t("al.p.pickMissionFor", { id: params.get("asset"), lang: (params.get("lang") || "").toUpperCase() })}</div>}
            {sparks.length > 0 && (
              <div className="grid sm:grid-cols-3 gap-3 mt-6" data-testid="lib-sparks">
                {sparks.map((s, i) => (
                  <button key={s.mission_id} onClick={() => setOpen(s.mission_id)} className={`nb-card p-4 text-start ${["bg-mint", "bg-sky", "bg-peach"][i]} hover:-translate-y-0.5 transition-transform`} data-testid={`lib-spark-${i}`}>
                    <div className="text-[11px] font-bold uppercase tracking-widest rtl:tracking-normal flex items-center gap-1"><Sparkles className="w-3 h-3" /> {t("lib.spark")} · {s.cefr}</div>
                    <div className="font-cabinet font-bold mt-1" dir="ltr">{s.title}</div>
                    <div className="text-xs text-gray-700 mt-1 line-clamp-2" dir="ltr">{s.learning_objective}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="nb-card p-4 mt-6 space-y-3" data-testid="lib-filters">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 shrink-0" />
                <input className="nb-input" placeholder={t("lib.searchPh")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="lib-search" />
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="lib-cefr-chips">
                {(facets?.cefr || []).map((c) => (
                  <button key={c} onClick={() => set("cefr", f.cefr === c ? "" : c)} className={`nb-tag text-xs ${f.cefr === c ? "bg-ink text-white" : "bg-white"}`} data-testid={`lib-cefr-${c}`}>{c}</button>
                ))}
                {["saved", "recent", "assigned"].map((s) => (
                  <button key={s} onClick={() => set("shelf", f.shelf === s ? "" : s)} className={`nb-tag text-xs ${f.shelf === s ? "bg-coral text-white" : "bg-butter"}`} data-testid={`lib-shelf-${s}`}>{t(`lib.shelf.${s}`)}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Sel k="theme" opts={facets?.theme || []} label={t("lib.f.theme")} />
                <Sel k="mode" opts={MODES} label={t("lib.f.mode")} />
                <Sel k="skill" opts={SKILLS} label={t("lib.f.skill")} />
                <Sel k="grouping" opts={facets?.grouping || []} label={t("lib.f.grouping")} />
                <Sel k="max_minutes" opts={[10, 15, 20, 30, 40].map((n) => ({ v: n, l: `≤ ${n} min` }))} label={t("lib.f.duration")} />
                <Sel k="strand" opts={facets?.curriculum_strand || []} label={t("lib.f.curriculum_strand")} />
                <Sel k="output" opts={["text", "voice", "file"].map((v) => ({ v, l: t(`lib.out.${v}`) }))} label={t("lib.f.output")} />
                <button onClick={() => { setF(EMPTY); setPage(1); }} className="nb-btn-outline py-1.5 text-sm" data-testid="lib-clear">{t("lib.clear")}</button>
              </div>
            </div>
            <div className="text-sm text-gray-600 mt-4" data-testid="lib-count">{t("lib.results", { n: res?.total ?? "…" })}</div>
            {err === "error" && <div className="nb-card bg-peach p-3 mt-2 text-sm flex items-center gap-3" data-testid="lib-error">{t("lib.failed")} <button onClick={() => { if (!facets) loadFacets(); search(); }} className="underline font-bold" data-testid="lib-retry">{t("lib.retry")}</button></div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2" data-testid="lib-results">
              {res?.items.map((m) => (
                <button key={m.mission_id} onClick={() => setOpen(m.mission_id)} className="nb-card p-4 text-start hover:-translate-y-0.5 transition-transform" data-testid={`lib-card-${m.mission_id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="nb-tag bg-mint">{m.cefr}</span>
                    {m.saved && <Star className="w-4 h-4 fill-current text-amber-500" />}
                  </div>
                  <div className="font-cabinet font-bold mt-2" dir="ltr">{m.title}</div>
                  <div className="text-xs text-gray-600 mt-1" dir="ltr">{m.communication_mode} · {m.primary_skill} · {m.duration_min} min · {m.grouping}</div>
                </button>
              ))}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-5" data-testid="lib-pager">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="nb-btn-outline py-1 px-2" aria-label="prev"><ChevronLeft className="w-4 h-4 rtl:rotate-180" /></button>
                <span className="text-sm" dir="ltr">{page} / {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="nb-btn-outline py-1 px-2" aria-label="next" data-testid="lib-next"><ChevronRight className="w-4 h-4 rtl:rotate-180" /></button>
              </div>
            )}
          </>
        )}
        {tab === "collections" && <CollectionsTab collections={collections} reload={loadCollections} />}
        {tab === "files" && <FilesTab collections={collections} reload={loadCollections} />}
      </div>
      <TemplateDetail id={open} onClose={() => setOpen(null)} collections={collections} onChanged={() => { search(); loadCollections(); }} />
    </AppLayout>
  );
}
